import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosSnackbar, SovosTab, SovosTabs, SovosTypography } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import AiFixPanel from "../ColumnMappingCanvas/steps/components/AiFixPanel";
import StagingDataGrid from "./StagingDataGrid";
import TranslationsList from "./TranslationsList";
import { APRS_WEBAPP_SOVI_AI_ENABLED } from "./featureFlags";
import {
    fixKey,
    getFailedFixKeys,
    partitionFixesForApplication,
    buildTranslationRuleName,
    canRequestAiFixes,
    waitForTranslationCompletion,
    watchTranslationUntilComplete,
} from "./aiFixApplication";

const SUB_TAB_GRID = "grid";
const SUB_TAB_TRANSLATIONS = "translations";

const collectApplicableFixes = (aiFixes, acceptedFixes, rejectedFixes, columnNameFilter) => {
    if (!aiFixes?.suggestions) return [];

    const candidates = [];
    for (const col of aiFixes.suggestions) {
        if (columnNameFilter && col.column_name !== columnNameFilter) continue;
        for (const fix of col.fixes || []) {
            const key = fixKey(fix.clb_id, col.column_name);
            if (acceptedFixes.has(key) || rejectedFixes.has(key)) continue;
            if (fix.status !== "applied" || fix.requires_review) continue;
            candidates.push({
                clbId: fix.clb_id,
                columnName: col.column_name,
                originalValue: fix.original_value ?? null,
                newValue: fix.new_value ?? null,
                fixMode: col.fix_mode ?? null,
            });
        }
    }
    return candidates;
};

const StagingDataPanel = ({
    profileId,
    projectId,
    mappingId,
    configSet,
    entityName,
    items = [],
    headerItems = [],
    metadata = {},
    canMutateTranslations = true,
    nonOwnerTooltip,
    aiSuggestionsEnabled = false,
    onFetchIssues,
    onFixCell,
    onProceed,
    proceedLabel,
    proceedDisabled,
    onSuggestFixes,
    onBulkAcceptFixes,
    onCreateStagingTranslation,
    onApplyStagingTranslation,
    onListStagingTranslations,
    onUpdateStagingTranslation,
    onToggleStagingTranslationState,
    onSoftDeleteStagingTranslation,
    onStagingDataChanged,
}) => {
    const { i18n } = useGlobalValue();
    const [activeTab, setActiveTab] = useState(SUB_TAB_GRID);
    const [issueSummary, setIssueSummary] = useState(null);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const [aiFixes, setAiFixes] = useState(null);
    const [loadingAiFixes, setLoadingAiFixes] = useState(false);
    const [acceptedFixes, setAcceptedFixes] = useState(new Set());
    const [rejectedFixes, setRejectedFixes] = useState(new Set());
    const [applyingFixes, setApplyingFixes] = useState(false);
    const [notification, setNotification] = useState(null);
    const [pendingRuleKeys, setPendingRuleKeys] = useState(new Set());
    const translationRuleIdsRef = useRef(new Map());
    const isMountedRef = useRef(true);
    const contextVersionRef = useRef(0);

    const showAiPanel = APRS_WEBAPP_SOVI_AI_ENABLED && aiSuggestionsEnabled && aiPanelOpen;

    const SYSTEM_COLUMNS = useMemo(() => new Set(["CLB_ImportID", "FieldMetadata"]), []);

    const customColumnHeaders = useMemo(
        () => headerItems.filter((h) => !SYSTEM_COLUMNS.has(h.headerName)),
        [headerItems, SYSTEM_COLUMNS],
    );

    const customColumnNames = useMemo(
        () => customColumnHeaders.map((h) => h.columnName),
        [customColumnHeaders],
    );

    const refreshIssues = useMemo(
        () => () => {
            if (
                !onFetchIssues ||
                !profileId ||
                !projectId ||
                !mappingId ||
                !configSet ||
                !entityName
            )
                return;
            setLoadingIssues(true);
            onFetchIssues(profileId, mappingId, configSet, entityName, projectId)
                .then((result) =>
                    setIssueSummary(result ? { ...result.summary, rows: result.rows || [] } : null),
                )
                .catch(() => setIssueSummary(null))
                .finally(() => setLoadingIssues(false));
        },
        [onFetchIssues, profileId, projectId, mappingId, configSet, entityName],
    );

    useEffect(() => {
        refreshIssues();
    }, [refreshIssues]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        contextVersionRef.current += 1;
        setAiFixes(null);
        setAcceptedFixes(new Set());
        setRejectedFixes(new Set());
        setAiPanelOpen(false);
        setPendingRuleKeys(new Set());
        translationRuleIdsRef.current.clear();
    }, [profileId, mappingId]);

    const suggestedFixCount = useMemo(() => {
        if (!aiFixes?.suggestions) return 0;
        return aiFixes.suggestions.reduce((acc, col) => {
            const pending = (col.fixes || []).filter((fix) => {
                const key = fixKey(fix.clb_id, col.column_name);
                return (
                    fix.status === "applied" &&
                    !fix.requires_review &&
                    !acceptedFixes.has(key) &&
                    !rejectedFixes.has(key)
                );
            });
            return acc + pending.length;
        }, 0);
    }, [aiFixes, acceptedFixes, rejectedFixes]);

    const handleSuggestFixes = useCallback(
        (columnNames) => {
            if (!onSuggestFixes || !profileId || !projectId) return;
            setAiPanelOpen(true);
            setLoadingAiFixes(true);
            setAiFixes(null);
            setAcceptedFixes(new Set());
            setRejectedFixes(new Set());
            onSuggestFixes(profileId, projectId, columnNames)
                .then((result) => setAiFixes(result || null))
                .catch(() => setAiFixes(null))
                .finally(() => setLoadingAiFixes(false));
        },
        [onSuggestFixes, profileId, projectId],
    );

    const applyCandidates = useCallback(
        async (candidates) => {
            if (!candidates.length) {
                return {
                    acceptedKeys: [],
                    failedCount: 0,
                    pendingCount: 0,
                    notStartedCount: 0,
                    changedOnFailure: false,
                };
            }

            const { translationRules, directFixes } = partitionFixesForApplication(
                candidates,
                items,
                metadata.total ?? items.length,
            );
            const acceptedKeys = [];
            let failedCount = 0;
            let pendingCount = 0;
            let notStartedCount = 0;
            let changedOnFailure = false;

            if (
                translationRules.length > 0 &&
                onCreateStagingTranslation &&
                onApplyStagingTranslation &&
                onListStagingTranslations
            ) {
                for (let ruleIndex = 0; ruleIndex < translationRules.length; ruleIndex += 1) {
                    const rule = translationRules[ruleIndex];
                    try {
                        const ruleKey = JSON.stringify([
                            rule.columnName,
                            rule.originalValue,
                            rule.newValue ?? "",
                        ]);
                        let created = translationRuleIdsRef.current.get(ruleKey);
                        if (!created) {
                            if (!onListStagingTranslations) {
                                failedCount += rule.fixes.length;
                                continue;
                            }
                            const existingRules = await onListStagingTranslations(
                                profileId,
                                projectId,
                            ).catch(() => null);
                            if (!Array.isArray(existingRules)) {
                                failedCount += rule.fixes.length;
                                continue;
                            }
                            created = existingRules.find(
                                (translation) =>
                                    translation.column_name === rule.columnName &&
                                    translation.operator === "eq" &&
                                    translation.find_value === rule.originalValue &&
                                    translation.replace_value === (rule.newValue ?? ""),
                            );
                            if (created?.id) {
                                translationRuleIdsRef.current.set(ruleKey, created);
                            }
                        }
                        if (!created) {
                            created = await onCreateStagingTranslation({
                                projectId,
                                importProfileId: profileId,
                                name: buildTranslationRuleName(rule),
                                columnName: rule.columnName,
                                operator: "eq",
                                findValue: rule.originalValue,
                                replaceValue: rule.newValue ?? "",
                            }).catch(() => null);
                            if (created?.id) {
                                translationRuleIdsRef.current.set(ruleKey, created);
                            }
                        }
                        if (!created?.id) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        const currentRules = await onListStagingTranslations(
                            profileId,
                            projectId,
                        ).catch(() => null);
                        if (!Array.isArray(currentRules)) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        let currentRule = currentRules.find(
                            (translation) => translation.id === created.id,
                        );
                        if (!currentRule) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        if (currentRule.state === "Inactive") {
                            currentRule = onToggleStagingTranslationState
                                ? await onToggleStagingTranslationState(
                                      currentRule.id,
                                      "Active",
                                  ).catch(() => null)
                                : null;
                            if (!currentRule) {
                                failedCount += rule.fixes.length;
                                continue;
                            }
                        }
                        if (currentRule.state !== "Active") {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        translationRuleIdsRef.current.set(ruleKey, currentRule);

                        const applyResult = await onApplyStagingTranslation(
                            currentRule.id,
                            profileId,
                            projectId,
                        ).catch(() => null);
                        if (!applyResult?.queued) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        const completed = onListStagingTranslations
                            ? await waitForTranslationCompletion({
                                  translationId: currentRule.id,
                                  listTranslations: onListStagingTranslations,
                                  profileId,
                                  projectId,
                                  previousRunAt: currentRule.last_run_at,
                              }).catch(() => null)
                            : null;
                        if (!completed) {
                            pendingCount += rule.fixes.length;
                            for (
                                let remainingIndex = ruleIndex + 1;
                                remainingIndex < translationRules.length;
                                remainingIndex += 1
                            ) {
                                notStartedCount +=
                                    translationRules[remainingIndex].fixes.length;
                            }
                            setPendingRuleKeys((prev) => new Set(prev).add(ruleKey));
                            const watcherContext = contextVersionRef.current;
                            const clearPendingRule = () => {
                                setPendingRuleKeys((prev) => {
                                    const next = new Set(prev);
                                    next.delete(ruleKey);
                                    return next;
                                });
                            };
                            (async () => {
                                const lateResult = await watchTranslationUntilComplete({
                                    translationId: currentRule.id,
                                    listTranslations: onListStagingTranslations,
                                    profileId,
                                    projectId,
                                    previousRunAt: currentRule.last_run_at,
                                    isCancelled: () =>
                                        !isMountedRef.current ||
                                        contextVersionRef.current !== watcherContext,
                                });
                                if (
                                    !isMountedRef.current ||
                                    contextVersionRef.current !== watcherContext
                                ) {
                                    return;
                                }
                                clearPendingRule();
                                if (!lateResult) {
                                    setNotification({
                                        type: "error",
                                        message: i18n`"${currentRule.name}" is still running or timed out. Refresh and retry if needed.`,
                                    });
                                    return;
                                }
                                if (lateResult.last_run_outcome === "SUCCESS") {
                                    setAcceptedFixes((prev) => {
                                        const next = new Set(prev);
                                        rule.fixes.forEach((fix) =>
                                            next.add(fixKey(fix.clbId, fix.columnName)),
                                        );
                                        return next;
                                    });
                                }
                                refreshIssues();
                                if (onStagingDataChanged) onStagingDataChanged();
                                setNotification({
                                    type:
                                        lateResult.last_run_outcome === "SUCCESS"
                                            ? "confirmation"
                                            : "error",
                                    message:
                                        lateResult.last_run_outcome === "SUCCESS"
                                            ? i18n`"${currentRule.name}" finished.`
                                            : i18n`"${currentRule.name}" did not complete successfully.`,
                                });
                            })();
                            break;
                        }
                        if (completed.last_run_outcome !== "SUCCESS") {
                            failedCount += rule.fixes.length;
                            changedOnFailure =
                                changedOnFailure || (completed.last_run_changed_rows ?? 0) > 0;
                            continue;
                        }
                        for (const fix of rule.fixes) {
                            acceptedKeys.push(fixKey(fix.clbId, fix.columnName));
                        }
                    } catch (_error) {
                        failedCount += rule.fixes.length;
                    }
                }
            } else if (translationRules.length > 0) {
                failedCount += translationRules.reduce(
                    (total, rule) => total + rule.fixes.length,
                    0,
                );
            }

            if (directFixes.length > 0 && onBulkAcceptFixes) {
                const fixes = directFixes.map((c) => ({
                    clbId: c.clbId,
                    column: c.columnName,
                    newValue: c.newValue ?? null,
                }));
                const result = await onBulkAcceptFixes(profileId, String(projectId), fixes).catch(
                    () => null,
                );
                if (!result || typeof result.accepted !== "number") {
                    failedCount += directFixes.length;
                    return {
                        acceptedKeys,
                        failedCount,
                        pendingCount,
                        notStartedCount,
                        changedOnFailure,
                    };
                }
                const failedFixKeys = getFailedFixKeys(result?.errors || []);
                failedCount += result?.failed ?? 0;
                if (failedFixKeys.size > 0) {
                    directFixes.forEach((fix) => {
                        const key = fixKey(fix.clbId, fix.columnName);
                        if (!failedFixKeys.has(key)) {
                            acceptedKeys.push(key);
                        }
                    });
                } else if (result.accepted === directFixes.length) {
                    directFixes.forEach((fix) => {
                        acceptedKeys.push(fixKey(fix.clbId, fix.columnName));
                    });
                }
            } else if (directFixes.length > 0) {
                failedCount += directFixes.length;
            }

            return {
                acceptedKeys,
                failedCount,
                pendingCount,
                notStartedCount,
                changedOnFailure,
            };
        },
        [
            onApplyStagingTranslation,
            onBulkAcceptFixes,
            onCreateStagingTranslation,
            onListStagingTranslations,
            onToggleStagingTranslationState,
            items,
            metadata.total,
            profileId,
            projectId,
            refreshIssues,
            onStagingDataChanged,
        ],
    );

    const handleBulkAcceptSafe = useCallback(async () => {
        if (!profileId || !projectId || !aiFixes?.suggestions) return;
        const candidates = collectApplicableFixes(aiFixes, acceptedFixes, rejectedFixes);
        if (!candidates.length) return;

        setApplyingFixes(true);
        try {
            const {
                acceptedKeys,
                failedCount,
                pendingCount,
                notStartedCount,
                changedOnFailure,
            } = await applyCandidates(candidates);
            if (acceptedKeys.length > 0) {
                setAcceptedFixes((prev) => {
                    const next = new Set(prev);
                    acceptedKeys.forEach((key) => next.add(key));
                    return next;
                });
            }
            if (acceptedKeys.length > 0 || changedOnFailure) {
                refreshIssues();
                if (onStagingDataChanged) onStagingDataChanged();
            }
            if (pendingCount > 0) {
                const notStartedSuffix =
                    notStartedCount > 0
                        ? ` ${i18n`${notStartedCount} additional AI fixes were not started — retry after completion.`}`
                        : "";
                setNotification({
                    type: "info",
                    message: `${i18n`${pendingCount} AI fixes are still queued. Wait for completion before applying more rules.`}${notStartedSuffix}`,
                });
            } else if (failedCount > 0) {
                setNotification({
                    type: "error",
                    message: i18n`${failedCount} AI fixes could not be applied.`,
                });
            }
        } catch (_error) {
            setNotification({
                type: "error",
                message: i18n`AI fixes could not be applied. No additional changes were queued.`,
            });
        } finally {
            setApplyingFixes(false);
        }
    }, [
        aiFixes,
        acceptedFixes,
        rejectedFixes,
        applyCandidates,
        profileId,
        projectId,
        refreshIssues,
        onStagingDataChanged,
    ]);

    const handleBulkApproveColumn = useCallback(
        async (columnName, fixes) => {
            if (!profileId || !projectId) return;
            const columnFixMode = aiFixes?.suggestions?.find(
                (suggestion) => suggestion.column_name === columnName,
            )?.fix_mode ?? null;
            const candidates = (fixes || [])
                .filter((fix) => {
                    const key = fixKey(fix.clb_id, columnName);
                    return (
                        fix.status === "applied" &&
                        !fix.requires_review &&
                        !acceptedFixes.has(key) &&
                        !rejectedFixes.has(key)
                    );
                })
                .map((fix) => ({
                    clbId: fix.clb_id,
                    columnName,
                    originalValue: fix.original_value ?? null,
                    newValue: fix.new_value ?? null,
                    fixMode: columnFixMode,
                }));
            if (!candidates.length) return;

            setApplyingFixes(true);
            try {
                const {
                    acceptedKeys,
                    failedCount,
                    pendingCount,
                    notStartedCount,
                    changedOnFailure,
                } = await applyCandidates(candidates);
                if (acceptedKeys.length > 0) {
                    setAcceptedFixes((prev) => {
                        const next = new Set(prev);
                        acceptedKeys.forEach((key) => next.add(key));
                        return next;
                    });
                }
                if (acceptedKeys.length > 0 || changedOnFailure) {
                    refreshIssues();
                    if (onStagingDataChanged) onStagingDataChanged();
                }
                if (pendingCount > 0) {
                    const notStartedSuffix =
                        notStartedCount > 0
                            ? ` ${i18n`${notStartedCount} additional AI fixes were not started — retry after completion.`}`
                            : "";
                    setNotification({
                        type: "info",
                        message: `${i18n`${pendingCount} AI fixes are still queued. Wait for completion before applying more rules.`}${notStartedSuffix}`,
                    });
                } else if (failedCount > 0) {
                    setNotification({
                        type: "error",
                        message: i18n`${failedCount} AI fixes could not be applied.`,
                    });
                }
            } catch (_error) {
                setNotification({
                    type: "error",
                    message: i18n`AI fixes could not be applied. No additional changes were queued.`,
                });
            } finally {
                setApplyingFixes(false);
            }
        },
        [
            acceptedFixes,
            aiFixes,
            applyCandidates,
            profileId,
            projectId,
            refreshIssues,
            rejectedFixes,
            onStagingDataChanged,
        ],
    );

    const handleRejectRule = useCallback((columnName, fixes) => {
        const nextRejectedKeys = (fixes || []).map((fix) => fixKey(fix.clb_id, columnName));
        if (!nextRejectedKeys.length) return;
        setRejectedFixes((prev) => {
            const next = new Set(prev);
            nextRejectedKeys.forEach((key) => next.add(key));
            return next;
        });
    }, []);

    const hasMappingContext = !!(mappingId && configSet && entityName);
    const aiActionsEnabled = canRequestAiFixes({ aiSuggestionsEnabled, onSuggestFixes });
    const handleTranslationApplied = useCallback(() => {
        refreshIssues();
        if (onStagingDataChanged) onStagingDataChanged();
    }, [onStagingDataChanged, refreshIssues]);

    return (
        <SovosBox
            sx={{
                display: "grid",
                gridTemplateColumns: showAiPanel ? "minmax(0, 1fr) 395px" : "minmax(0, 1fr)",
                gap: 0,
                alignItems: "stretch",
                minHeight: "calc(100vh - 280px)",
            }}
        >
            <SovosBox sx={{ minWidth: 0, p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <SovosTabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
                    <SovosTab value={SUB_TAB_GRID} label={i18n`Staging data`} />
                    <SovosTab value={SUB_TAB_TRANSLATIONS} label={i18n`Translations`} />
                </SovosTabs>

                {activeTab === SUB_TAB_GRID &&
                    (!hasMappingContext ? (
                        <SovosBox
                            sx={{
                                p: 4,
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                                {i18n`Save a mapping first to scan the staging data for issues.`}
                            </SovosTypography>
                        </SovosBox>
                    ) : loadingIssues && !issueSummary ? (
                        <SovosBox
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1.5,
                                py: 6,
                            }}
                        >
                            <CircularProgress size={28} />
                            <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                                {i18n`Scanning for data issues…`}
                            </SovosTypography>
                        </SovosBox>
                    ) : (
                        <StagingDataGrid
                            items={items}
                            columnHeaders={customColumnHeaders}
                            issueSummary={issueSummary}
                            loading={loadingIssues}
                            profileId={profileId}
                            projectId={projectId}
                            canMutate={canMutateTranslations}
                            nonOwnerTooltip={nonOwnerTooltip}
                            aiSuggestionsEnabled={aiActionsEnabled}
                            suggestedFixCount={suggestedFixCount}
                            onFixCell={onFixCell}
                            onRefreshIssues={refreshIssues}
                            onProceed={onProceed}
                            proceedLabel={proceedLabel}
                            proceedDisabled={proceedDisabled}
                            onOpenAiPanel={() => {
                                setAiPanelOpen(true);
                                if (!aiFixes && !loadingAiFixes) {
                                    handleSuggestFixes();
                                }
                            }}
                        />
                    ))}

                {activeTab === SUB_TAB_TRANSLATIONS && (
                    <TranslationsList
                        profileId={profileId}
                        projectId={projectId}
                        columnOptions={customColumnNames}
                        canMutate={canMutateTranslations}
                        nonOwnerTooltip={nonOwnerTooltip}
                        onList={onListStagingTranslations}
                        onCreate={onCreateStagingTranslation}
                        onUpdate={onUpdateStagingTranslation}
                        onToggleState={onToggleStagingTranslationState}
                        onSoftDelete={onSoftDeleteStagingTranslation}
                        onApply={onApplyStagingTranslation}
                        onApplyCompleted={handleTranslationApplied}
                    />
                )}
            </SovosBox>

            {showAiPanel && (
                <AiFixPanel
                    acceptedFixes={acceptedFixes}
                    aiFixes={aiFixes}
                    canApply={canMutateTranslations && !applyingFixes && pendingRuleKeys.size === 0}
                    issueSummary={issueSummary}
                    loadingAiFixes={loadingAiFixes || applyingFixes}
                    onBulkAcceptSafe={handleBulkAcceptSafe}
                    onBulkApproveColumn={handleBulkApproveColumn}
                    onClose={() => setAiPanelOpen(false)}
                    onRejectRule={handleRejectRule}
                    onRequestFixes={handleSuggestFixes}
                    rejectedFixes={rejectedFixes}
                />
            )}
            <SovosSnackbar
                open={!!notification}
                message={notification?.message}
                type={notification?.type}
                onClose={() => setNotification(null)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </SovosBox>
    );
};

export default StagingDataPanel;
